import { cn } from "@/lib/utils";
import { ticksEixo } from "./escala";
import type { Serie } from "./GraficoColunas";

export interface ColunaEvolucao {
  /** Identificador da coluna (ex.: competência "2026-09"). */
  chave: string;
  /** Rótulo do eixo X (ex.: "set/26"). */
  rotulo: string;
  valores: Record<string, number>;
}

interface Props {
  colunas: ColunaEvolucao[];
  series: Serie[];
  /** Coluna destacada (fundo laranja, opacidade cheia e total acima). */
  selecionada: string;
  onSelecionar: (chave: string) => void;
  /** Linha tracejada de referência (ex.: média de 12 meses); null não desenha. */
  media?: number | null;
  formatarValor: (v: number) => string;
  formatarEixo: (v: number) => string;
  /** Rótulo do total acima da coluna selecionada; padrão = formatarEixo. */
  formatarTotal?: (v: number) => string;
  altura?: number;
}

/** Altura da linha de rótulos do eixo X, em px (o eixo Y desconta essa faixa). */
const ALTURA_EIXO_X = 26;

/**
 * Colunas empilhadas clicáveis do dashboard: a selecionada fica em destaque e
 * as demais a 50%; clicar numa coluna a seleciona. Topo arredondado só no
 * segmento de cima e 2px de vão na cor da superfície entre segmentos.
 */
export const GraficoEvolucao = ({
  colunas,
  series,
  selecionada,
  onSelecionar,
  media = null,
  formatarValor,
  formatarEixo,
  formatarTotal = formatarEixo,
  altura = 280,
}: Props) => {
  const total = (c: ColunaEvolucao) => series.reduce((s, x) => s + (c.valores[x.chave] ?? 0), 0);
  const ticks = ticksEixo(Math.max(0, media ?? 0, ...colunas.map(total)));
  const topo = ticks[ticks.length - 1] || 1;
  const pct = (v: number) => `${Math.min(100, (v / topo) * 100)}%`;

  return (
    <div className="flex tabular-nums" style={{ height: altura }}>
      {/* Eixo Y: os rótulos ficam centrados em cada linha de grade */}
      <div className="relative w-[72px] shrink-0 text-[11px] text-muted-foreground" style={{ marginBottom: ALTURA_EIXO_X }}>
        {ticks.map((t, i) => (
          <span
            key={t}
            className="absolute right-2.5 translate-y-1/2 whitespace-nowrap"
            style={{ bottom: `${(i / (ticks.length - 1)) * 100}%` }}
          >
            {formatarEixo(t)}
          </span>
        ))}
      </div>

      <div className="flex min-w-0 flex-1 flex-col">
        <div className="relative flex flex-1 border-b border-border">
          {ticks.slice(1).map((t, i) => (
            <div
              key={t}
              aria-hidden
              className="pointer-events-none absolute inset-x-0 border-t border-border-soft"
              style={{ bottom: `${((i + 1) / (ticks.length - 1)) * 100}%` }}
            />
          ))}
          {media != null && media > 0 && (
            <div
              aria-hidden
              className="pointer-events-none absolute inset-x-0 z-10 border-t-[1.5px] border-dashed border-muted-foreground/70"
              style={{ bottom: pct(media) }}
            />
          )}

          {colunas.map((c, i) => {
            const sel = c.chave === selecionada;
            const soma = total(c);
            const segmentos = series.filter((s) => (c.valores[s.chave] ?? 0) > 0);
            // Tooltip alinhado à borda nas colunas das pontas, para não sair do card.
            const lado = i < 2 ? "left-0" : i > colunas.length - 3 ? "right-0" : "left-1/2 -translate-x-1/2";
            return (
              <div key={c.chave} className="group relative flex h-full flex-1">
                <button
                  type="button"
                  onClick={() => onSelecionar(c.chave)}
                  aria-pressed={sel}
                  aria-label={`${c.rotulo}: ${formatarValor(soma)}`}
                  className={cn(
                    "flex h-full w-full flex-col items-center justify-end rounded-t-md transition-colors",
                    sel ? "bg-primary/[0.06]" : "opacity-50 hover:bg-muted-soft hover:opacity-80",
                  )}
                >
                  <span className={cn("mb-1.5 whitespace-nowrap text-[11px] font-semibold", !sel && "invisible")}>
                    {formatarTotal(soma)}
                  </span>
                  <span className="flex w-[min(28px,60%)] flex-col-reverse" style={{ height: pct(soma) }}>
                    {segmentos.map((s, k) => (
                      <span
                        key={s.chave}
                        className={cn("w-full border-t-2 border-card", k === segmentos.length - 1 && "rounded-t")}
                        style={{ flex: `0 0 ${((c.valores[s.chave] ?? 0) / soma) * 100}%`, background: s.cor }}
                      />
                    ))}
                  </span>
                </button>

                <div
                  role="tooltip"
                  className={cn(
                    "pointer-events-none absolute top-2 z-20 hidden min-w-[190px] rounded-md border bg-popover px-3 py-2 text-xs shadow-dropdown group-hover:block",
                    lado,
                  )}
                >
                  <p className="mb-1 font-medium text-foreground">{c.rotulo}</p>
                  {series.length > 1 &&
                    [...series].reverse().map((s) => (
                      <p key={s.chave} className="flex items-center justify-between gap-4 text-muted-foreground">
                        <span className="flex items-center gap-1.5">
                          <span className="h-2 w-2 rounded-sm" style={{ background: s.cor }} aria-hidden />
                          {s.rotulo}
                        </span>
                        <span className="text-foreground">{formatarValor(c.valores[s.chave] ?? 0)}</span>
                      </p>
                    ))}
                  <p
                    className={cn(
                      "flex justify-between gap-4 font-medium text-foreground",
                      series.length > 1 && "mt-1 border-t pt-1",
                    )}
                  >
                    <span>{series.length > 1 ? "Total" : series[0]?.rotulo}</span>
                    <span>{formatarValor(soma)}</span>
                  </p>
                </div>
              </div>
            );
          })}
        </div>

        <div className="flex items-center text-[11px] text-muted-foreground" style={{ height: ALTURA_EIXO_X }}>
          {colunas.map((c) => (
            <span
              key={c.chave}
              className={cn("flex-1 text-center", c.chave === selecionada && "font-bold text-foreground")}
            >
              {c.rotulo}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
};
